import { useCallback, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { fetchTabLockStatus } from '../../../../store/investor/action';
import { TabModule } from '../../../../utils/constants/commonConstants';
import type { GstSubTabEntry } from './GstSubTabBar';

export const OVERVIEW_PILL_ID = 'gstOverview';
export const REVENUE_PILL_ID = 'revenueAnalysis';
export const EXPENSES_PILL_ID = 'expensesAnalysis';

type TabLockEntry = { isLocked?: boolean; lockedMessage?: string } | null | undefined;

export interface GstOverviewLogic {
    orgId: string;
    gstSubTabs: GstSubTabEntry[];
    noDataPills: Record<string, { tooltip?: string }>;
    activePill: string;
    handlePillSelect: (pillId: string) => void;
    modulePollingList: string[];
}

export function useGstOverviewLogic(): GstOverviewLogic {
    const params = useParams();
    const orgId = params.id || '';
    const dispatch = useAppDispatch();
    const [searchParams, setSearchParams] = useSearchParams();

    const investorData = useAppSelector((state) => state.investor);
    const dashboardConfig: any = investorData.config;
    const modulePollingList = useAppSelector((state) => state.investor.modulePollingList);
    const tabLockStatus = useAppSelector((state) => state.investor.tabLockStatus) as Record<string, TabLockEntry> | null;

    const gstSubTabs = useMemo<GstSubTabEntry[]>(() => {
        const gstTab = dashboardConfig?.tabs?.find((t: any) => t.id === TabModule.GST);
        return (gstTab?.subTabs ?? []) as GstSubTabEntry[];
    }, [dashboardConfig?.tabs]);

    const noDataPills = useMemo<Record<string, { tooltip?: string }>>(() => {
        const result: Record<string, { tooltip?: string }> = {};
        const revenueEntry = tabLockStatus?.[TabModule.REVENUE];
        const expensesEntry = tabLockStatus?.[TabModule.VENDOR_EXPENSES];
        // Pessimistic default: treat the pill as locked unless tabLockStatus
        // explicitly tells us isLocked === false. This avoids the "flash of
        // enabled, clickable pills" on reload while the first fetch is in
        // flight.
        if (revenueEntry?.isLocked !== false) {
            result[REVENUE_PILL_ID] = { tooltip: revenueEntry?.lockedMessage };
        }
        if (expensesEntry?.isLocked !== false) {
            result[EXPENSES_PILL_ID] = { tooltip: expensesEntry?.lockedMessage };
        }
        return result;
    }, [tabLockStatus]);

    // Once godzilla hides legacy revenue/vendorExpenses from the sidebar, the
    // TabSelector's own fetch stops covering them — trigger it here instead.
    useEffect(() => {
        if (!orgId) return;
        dispatch(
            fetchTabLockStatus({
                tabsList: [TabModule.REVENUE, TabModule.VENDOR_EXPENSES],
                assesseeOrgId: orgId,
            }),
        );
    }, [dispatch, orgId]);

    const isPillSelectable = useCallback(
        (pillId: string) => {
            const pill = gstSubTabs.find((s) => s.id === pillId);
            if (!pill) return false;
            if (pill.disabled) return false;
            if (noDataPills[pillId]) return false;
            return true;
        },
        [gstSubTabs, noDataPills],
    );

    const subTabFromUrl = searchParams.get('subTab') || '';
    const activePill = useMemo(() => {
        if (subTabFromUrl && isPillSelectable(subTabFromUrl)) return subTabFromUrl;
        const gstTab = dashboardConfig?.tabs?.find((t: any) => t.id === TabModule.GST);
        if (gstTab?.selectedSubTabId && isPillSelectable(gstTab.selectedSubTabId)) {
            return gstTab.selectedSubTabId as string;
        }
        return gstSubTabs.find((s) => isPillSelectable(s.id))?.id ?? OVERVIEW_PILL_ID;
    }, [subTabFromUrl, gstSubTabs, dashboardConfig?.tabs, isPillSelectable]);

    // Backfill / rewrite `?subTab` via `replace` so neither case adds history.
    // Functional setter avoids stale-closure races with the sibling `?dataType` writer.
    useEffect(() => {
        if (!activePill) return;
        if (subTabFromUrl === activePill) return;
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.set('subTab', activePill);
                return next;
            },
            { replace: true },
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activePill, subTabFromUrl]);

    const handlePillSelect = useCallback(
        (pillId: string) => {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set('subTab', pillId);
                return next;
            });
        },
        [setSearchParams],
    );

    return { orgId, gstSubTabs, noDataPills, activePill, handlePillSelect, modulePollingList };
}
